<script setup lang="ts">
import type { MediaSummary, SourceCategory } from '../../shared/contracts'
import { AlertCircle, ArrowRight, Check, ChevronDown, Layers3, Plus, RefreshCw } from '@lucide/vue'
import { onClickOutside } from '@vueuse/core'
import { computed, nextTick, onActivated, onMounted, ref, watch } from 'vue'
import HeroBanner from '@/components/home/HeroBanner.vue'
import MediaGrid from '@/components/media/MediaGrid.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import MediaSkeleton from '@/components/ui/MediaSkeleton.vue'
import { useHorizontalSwipe } from '@/composables/useHorizontalSwipe'
import { api } from '@/services/api'
import { useAppStore } from '@/stores/app'
import { useContentSourcesStore } from '@/stores/contentSources'
import { useLibraryStore } from '@/stores/library'
import { centeredScrollLeft } from '@/utils/horizontalScroll'
import { heroQuality, mergeMediaResults } from '@/utils/media'
import { MAX_ENABLED_SOURCES } from '../../shared/content-sources'

defineOptions({ name: 'HomeView' })

const library = useLibraryStore()
const appStore = useAppStore()
const contentSources = useContentSourcesStore()
const BROWSE_STORAGE_KEY = 'cineflow:home-browse:v1'

function loadBrowseState() {
    try {
        const value = JSON.parse(localStorage.getItem(BROWSE_STORAGE_KEY) || '{}') as { source?: string, category?: string }
        return {
            source: value.source && value.source !== '__all__' ? value.source : '',
            category: value.category || 'all',
        }
    }
    catch {
        return { source: '', category: 'all' }
    }
}

const savedBrowse = loadBrowseState()
const items = ref<MediaSummary[]>([])
const hero = ref<MediaSummary>()
const heroSourceId = ref('')
const loading = ref(true)
const initialized = ref(false)
const refreshing = ref(false)
const error = ref('')
const partial = ref(false)
const selectedSource = ref(savedBrowse.source)
const selectedCategory = ref(savedBrowse.category)
const sourceCategories = ref<SourceCategory[]>([])
const categoryLoading = ref(false)
const categoryError = ref('')
const sourceMenuOpen = ref(false)
const sourceMenu = ref<HTMLElement>()
const categoryScroller = ref<HTMLElement>()
const browseCache = new Map<string, { items: MediaSummary[], partial: boolean, updatedAt: number }>()
let loadRequest = 0
let categoryRequest = 0
let browseReady = false
let activatedOnce = false
let lastLoadedAt = 0
const failedSources = new Set<string>()
const availableSources = computed(() => {
    const merged = new Map([...appStore.activeSources, ...contentSources.sources].map(item => [item.id, item]))
    return [...merged.values()].slice(0, MAX_ENABLED_SOURCES)
})
const categoryFilters = computed(() => selectedSource.value
    ? [{ id: 'all', name: '全部', sourceId: selectedSource.value }, ...sourceCategories.value]
    : [{ id: 'all', name: '全部', sourceId: '' }])
const gridItems = computed(() => items.value.filter(item => item.id !== hero.value?.id || item.sourceId !== hero.value.sourceId))
const continueWatching = computed(() => library.history.slice(0, 6).map(record => record.media))
const selectedSourceName = computed(() => availableSources.value.find(item => item.id === selectedSource.value)?.name || '选择内容来源')
const selectedCategoryName = computed(() => categoryFilters.value.find(item => item.id === selectedCategory.value)?.name || '全部内容')
const browseTitle = computed(() => selectedCategory.value === 'all' ? '全部内容' : selectedCategoryName.value)
const browsePath = '/'

onClickOutside(sourceMenu, () => sourceMenuOpen.value = false)

function persistBrowseState() {
    localStorage.setItem(BROWSE_STORAGE_KEY, JSON.stringify({ source: selectedSource.value, category: selectedCategory.value }))
}

function currentBrowseCacheKey() {
    return `${selectedSource.value}:${selectedCategory.value}`
}

function ensureHeroForCurrentSource(candidates: MediaSummary[]) {
    if (heroSourceId.value === selectedSource.value && hero.value) { return }
    heroSourceId.value = selectedSource.value
    hero.value = [...candidates].sort((left, right) => heroQuality(right) - heroQuality(left))[0]
}

function centerSelectedCategory(behavior: ScrollBehavior = 'smooth') {
    const scroller = categoryScroller.value
    if (!scroller) { return }
    const selected = [...scroller.querySelectorAll<HTMLElement>('[data-category-id]')]
        .find(item => item.dataset.categoryId === selectedCategory.value)
    if (!selected) { return }
    const scrollerRect = scroller.getBoundingClientRect()
    const selectedRect = selected.getBoundingClientRect()
    const left = centeredScrollLeft({
        scrollLeft: scroller.scrollLeft,
        scrollWidth: scroller.scrollWidth,
        viewportWidth: scroller.clientWidth,
        itemLeft: selectedRect.left - scrollerRect.left,
        itemWidth: selectedRect.width,
    })
    scroller.scrollTo({ left, behavior })
}

async function load() {
    const requestId = ++loadRequest
    const cacheKey = currentBrowseCacheKey()
    const cached = browseCache.get(cacheKey)
    if (cached) {
        items.value = cached.items
        ensureHeroForCurrentSource(cached.items)
        partial.value = cached.partial
        initialized.value = true
        loading.value = false
        lastLoadedAt = cached.updatedAt
        failedSources.clear()
        if (Date.now() - cached.updatedAt < 30_000) {
            refreshing.value = false
            return
        }
    }
    const initialRequest = !initialized.value
    if (initialRequest) { loading.value = true }
    else {
        refreshing.value = true
        if (!cached) { items.value = [] }
    }
    error.value = ''
    try {
        if (!selectedSource.value) {
            items.value = []
            heroSourceId.value = ''
            hero.value = undefined
            return
        }
        const category = selectedCategory.value === 'all' ? undefined : selectedCategory.value
        const localSource = contentSources.findSource(selectedSource.value)
        const results = await Promise.allSettled([localSource
            ? api.discoverSources([localSource], { category })
            : api.discover({ source: selectedSource.value, category })])
        if (requestId !== loadRequest) { return }
        const successful = results.filter(result => result.status === 'fulfilled')
        if (!successful.length) {
            const failed = results.find(result => result.status === 'rejected')
            throw failed?.reason instanceof Error ? failed.reason : new Error('内容来源暂时不可用')
        }
        const nextItems = mergeMediaResults(successful.map(result => result.value.items))
        if (!nextItems.length && successful.every(result => result.value.partial)) {
            const message = successful.flatMap(result => result.value.errors || [])[0]?.message
            throw new Error(message || '当前内容来源暂时不可用')
        }
        items.value = nextItems
        ensureHeroForCurrentSource(nextItems)
        lastLoadedAt = Date.now()
        partial.value = results.some(result => result.status === 'rejected' || result.value.partial)
        browseCache.delete(cacheKey)
        browseCache.set(cacheKey, { items: nextItems, partial: partial.value, updatedAt: lastLoadedAt })
        while (browseCache.size > 24) {
            const oldest = browseCache.keys().next().value
            if (!oldest) { break }
            browseCache.delete(oldest)
        }
        failedSources.clear()
    }
    catch (caught) {
        if (selectedSource.value) {
            failedSources.add(selectedSource.value)
            const fallback = availableSources.value.find(source => !failedSources.has(source.id))
            if (fallback) {
                selectSource(fallback.id, true)
                return
            }
        }
        if (initialRequest || !items.value.length) { error.value = caught instanceof Error ? caught.message : '内容加载失败' }
        else { partial.value = true }
    }
    finally {
        if (requestId === loadRequest) {
            loading.value = false
            refreshing.value = false
            initialized.value = true
        }
    }
}

async function loadCategories() {
    const requestId = ++categoryRequest
    sourceCategories.value = []
    categoryError.value = ''
    if (!selectedSource.value) { return }
    categoryLoading.value = true
    try {
        const localSource = contentSources.findSource(selectedSource.value)
        const response = localSource
            ? await api.categoriesFromSource(localSource)
            : await api.categories(selectedSource.value)
        if (requestId === categoryRequest) {
            sourceCategories.value = response.categories
            if (selectedCategory.value !== 'all' && !response.categories.some(item => item.id === selectedCategory.value)) {
                selectedCategory.value = 'all'
                persistBrowseState()
                load()
            }
            await nextTick()
            centerSelectedCategory('auto')
        }
    }
    catch {
        if (requestId === categoryRequest) {
            sourceCategories.value = []
            categoryError.value = '分类加载失败，点击重试'
        }
    }
    finally {
        if (requestId === categoryRequest) { categoryLoading.value = false }
    }
}

function selectCategory(id: string) {
    if (selectedCategory.value === id) { return }
    selectedCategory.value = id
    persistBrowseState()
    load()
    nextTick(() => centerSelectedCategory())
}

function selectSource(id: string, preserveFailures = false) {
    if (selectedSource.value === id) {
        sourceMenuOpen.value = false
        return
    }
    selectedSource.value = id
    heroSourceId.value = id
    hero.value = undefined
    if (!preserveFailures) { failedSources.clear() }
    selectedCategory.value = 'all'
    sourceMenuOpen.value = false
    persistBrowseState()
    loadCategories()
    load()
}

function onCategoryWheel(event: WheelEvent) {
    if (!categoryScroller.value) { return }
    const scroller = categoryScroller.value
    if (scroller.scrollWidth <= scroller.clientWidth) { return }
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
    const canScroll = delta < 0 ? scroller.scrollLeft > 0 : scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth
    if (!canScroll) { return }
    event.preventDefault()
    scroller.scrollLeft += delta
}

function switchCategoryBySwipe(direction: -1 | 1) {
    if (refreshing.value || categoryLoading.value) { return }
    const index = categoryFilters.value.findIndex(item => item.id === selectedCategory.value)
    const next = categoryFilters.value[index + direction]
    if (next) { selectCategory(next.id) }
}

const categorySwipe = useHorizontalSwipe(switchCategoryBySwipe)

watch(availableSources, (sources) => {
    const source = sources[0]
    if (!browseReady || selectedSource.value || !source) { return }
    selectSource(source.id)
})

onMounted(async () => {
    await appStore.initialize()
    browseReady = true
    const firstSource = availableSources.value[0]
    if (selectedSource.value && !availableSources.value.some(item => item.id === selectedSource.value)) {
        selectedSource.value = firstSource?.id || ''
        selectedCategory.value = 'all'
        persistBrowseState()
    }
    else if (!selectedSource.value && firstSource) {
        selectedSource.value = firstSource.id
        selectedCategory.value = 'all'
        persistBrowseState()
    }
    if (selectedSource.value) { loadCategories() }
    load()
    nextTick(() => centerSelectedCategory('auto'))
})
onActivated(() => {
    if (!activatedOnce) {
        activatedOnce = true
        return
    }
    const firstSource = availableSources.value[0]
    if (selectedSource.value && !availableSources.value.some(item => item.id === selectedSource.value)) {
        if (firstSource) { selectSource(firstSource.id) }
        else {
            selectedSource.value = ''
            selectedCategory.value = 'all'
            items.value = []
            heroSourceId.value = ''
            hero.value = undefined
            persistBrowseState()
        }
        return
    }
    nextTick(() => centerSelectedCategory('auto'))
    if (initialized.value && Date.now() - lastLoadedAt > 45_000) { void load() }
})
</script>

<template>
    <div>
        <template v-if="loading && !initialized">
            <div class="grid grid-cols-[minmax(0,1fr)_minmax(116px,38vw)] mx-auto max-w-[1480px] min-h-[260px] animate-pulse items-center gap-5 px-5 py-10 lg:grid-cols-[minmax(0,1fr)_340px] sm:grid-cols-[minmax(0,1fr)_minmax(190px,32vw)] lg:min-h-[620px] sm:min-h-[520px] lg:gap-20 sm:gap-10 lg:px-12 lg:py-16 sm:px-8 sm:py-14">
                <div class="min-w-0">
                    <div class="h-3.5 w-24 rounded-full bg-[var(--c-skeleton)]" />
                    <div class="mt-6 h-14 max-w-xl w-4/5 rounded-lg bg-[var(--c-skeleton)] sm:h-20" />
                    <div class="mt-6 h-4 max-w-sm w-2/3 rounded-full bg-[var(--c-skeleton)]" />
                </div>
                <div class="aspect-[2/3] w-full rounded-[1.375rem] bg-[var(--c-skeleton)] sm:rounded-[1.75rem]" />
            </div>
            <div class="page-shell pt-12 lg:pt-16">
                <MediaSkeleton />
            </div>
        </template>
        <template v-else-if="error && !initialized">
            <div class="page-shell">
                <EmptyState title="内容没有加载出来" :description="error">
                    <button class="mt-5 btn-ghost" @click="load()">
                        <RefreshCw class="size-4" />重新加载
                    </button>
                </EmptyState>
            </div>
        </template>
        <template v-else>
            <HeroBanner v-if="hero" :media="hero" :from="browsePath" />

            <div class="page-shell">
                <div v-if="partial" class="mb-8 flex items-center gap-3 border-y border-[var(--c-warning-bg)] py-3 text-xs text-[var(--c-warning)]">
                    <AlertCircle class="size-4 shrink-0" /> 部分内容来源暂时不可用，已展示其余结果
                </div>

                <section v-if="continueWatching.length" class="mb-16">
                    <div class="mb-7 flex items-end justify-between border-b border-[var(--c-border)] pb-4">
                        <div>
                            <p class="mb-2 text-[10px] text-[var(--accent)] font-700 tracking-[.14em] uppercase">
                                继续播放
                            </p><h2 class="section-title">
                                继续观看
                            </h2>
                        </div>
                        <RouterLink to="/library?tab=history" class="flex items-center gap-2 text-xs text-[var(--c-text-muted)] transition hover:text-[var(--c-text)]">
                            观看历史 <ArrowRight class="size-3.5" />
                        </RouterLink>
                    </div>
                    <MediaGrid :items="continueWatching" :records="library.history" :from="browsePath" show-playback-status />
                </section>

                <section>
                    <div class="mb-7 flex flex-wrap items-end justify-between gap-5 border-b border-[var(--c-border)] pb-4">
                        <div>
                            <p class="mb-2 text-[10px] text-[var(--accent)] font-700 tracking-[.14em] uppercase">
                                浏览片库
                            </p><h2 class="section-title">
                                {{ browseTitle }}
                            </h2>
                        </div>
                        <div class="min-w-0 w-full flex flex-col gap-3 lg:w-auto lg:flex-row lg:items-center lg:gap-5">
                            <div ref="sourceMenu" class="relative w-full shrink-0 sm:w-auto">
                                <button class="h-11 w-full flex items-center gap-2 border border-[var(--c-border)] rounded-full bg-[var(--c-fill-subtle)] px-4 text-[15px] text-[var(--c-text-soft)] transition sm:w-44 hover:border-[var(--c-border-strong)] hover:bg-[var(--c-fill-hover)] hover:text-[var(--c-text)] focus-ring" :aria-expanded="sourceMenuOpen" aria-haspopup="listbox" @click="sourceMenuOpen = !sourceMenuOpen">
                                    <Layers3 class="size-4 shrink-0 text-[var(--accent)]" />
                                    <span class="min-w-0 flex-1 truncate text-left">{{ selectedSourceName }}</span>
                                    <ChevronDown class="size-4 shrink-0 transition" :class="sourceMenuOpen && 'rotate-180'" />
                                </button>
                                <Transition name="source-menu">
                                    <div v-if="sourceMenuOpen" class="absolute left-0 top-[calc(100%+.5rem)] z-20 max-h-72 min-w-52 w-full overflow-y-auto border border-[var(--c-border)] rounded-2xl bg-[var(--c-surface-raised)] p-1.5 shadow-[var(--c-shadow)] shadow-xl sm:left-auto sm:right-0" role="listbox" aria-label="选择内容来源">
                                        <button v-for="source in availableSources" :key="source.id" class="min-h-10 w-full flex items-center gap-3 rounded-xl px-3 text-left text-sm transition hover:bg-[var(--c-fill-hover)] focus-ring" :class="selectedSource === source.id ? 'text-[var(--c-text)]' : 'text-[var(--c-text-muted)]'" role="option" :aria-selected="selectedSource === source.id" @click="selectSource(source.id)">
                                            <Check class="size-4 shrink-0 transition" :class="selectedSource === source.id ? 'opacity-100 text-[var(--accent)]' : 'opacity-0'" />
                                            <span class="truncate">{{ source.name }}</span>
                                        </button>
                                        <p v-if="!availableSources.length" class="px-3 py-2 text-sm text-[var(--c-text-muted)]">
                                            暂无可用来源
                                        </p>
                                    </div>
                                </Transition>
                            </div>
                            <div ref="categoryScroller" class="category-scroller scrollbar-none min-w-0 w-full flex gap-6 overflow-x-auto overflow-y-hidden lg:max-w-[52vw]" @wheel="onCategoryWheel">
                                <RefreshCw v-if="categoryLoading || refreshing" class="my-auto size-4 shrink-0 animate-spin text-[var(--c-text-muted)]" />
                                <button v-for="item in categoryFilters" :key="item.id" :data-category-id="item.id" class="relative min-h-11 shrink-0 rounded-sm px-0.5 text-[15px] font-600 transition lg:min-h-10 lg:text-base focus-ring" :class="selectedCategory === item.id ? 'text-[var(--c-text)] after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-[var(--accent)]' : 'text-[var(--c-text-muted)] hover:text-[var(--c-text)]'" :aria-pressed="selectedCategory === item.id" @click="selectCategory(item.id)">
                                    {{ item.name }}
                                </button>
                                <button v-if="categoryError" class="min-h-11 flex shrink-0 items-center gap-1.5 rounded-full px-2 text-sm text-[var(--c-warning)] transition hover:text-[var(--c-text)] focus-ring" @click="loadCategories">
                                    <RefreshCw class="size-3.5" />{{ categoryError }}
                                </button>
                                <span v-else-if="selectedSource && !categoryLoading && !sourceCategories.length" class="min-h-11 flex shrink-0 items-center text-sm text-[var(--c-text-faint)]">
                                    该来源未提供更多分类
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="swipe-pane" @touchstart="categorySwipe.onTouchStart" @touchend="categorySwipe.onTouchEnd" @touchcancel="categorySwipe.onTouchCancel">
                        <MediaSkeleton v-if="refreshing && !items.length" />
                        <MediaGrid v-else-if="gridItems.length" :items="gridItems" :records="library.history" :from="browsePath" />
                        <EmptyState v-else title="这个分类暂时没有内容" description="可以切换分类，或在设置中添加其他内容来源。">
                            <RouterLink to="/settings" class="mt-5 btn-ghost">
                                <Plus class="size-4" />添加内容来源
                            </RouterLink>
                        </EmptyState>
                    </div>
                </section>
            </div>
        </template>
    </div>
</template>

<style scoped>
.category-scroller {
    overscroll-behavior-x: contain;
    touch-action: pan-x pan-y;
    -webkit-overflow-scrolling: touch;
}

.swipe-pane {
    min-height: max(18rem, 55dvh);
    touch-action: pan-y;
    overscroll-behavior-x: none;
    user-select: none;
}

.source-menu-enter-active,
.source-menu-leave-active {
    transition:
        opacity 160ms ease,
        transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: top right;
}

.source-menu-enter-from,
.source-menu-leave-to {
    opacity: 0;
    transform: translate3d(0, -6px, 0) scale(0.98);
}
</style>
